import { AfterViewInit, Component, effect, ElementRef, input, OnDestroy, output, viewChild } from '@angular/core';
import { get } from 'lodash';
import { IVideoPlayer } from '../../embedded-video-player.interface';

interface ITwitchPlayer {
  addEventListener(event: string, callback: () => void): void;
  getCurrentTime(): number;
  getDuration(): number;
  pause(): void;
  play(): void;
  seek(time: number): void;
  setMuted(muted: boolean): void;
  setVolume(volume: number): void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    Twitch?: {
      Player: new (
        elementId: string,
        options: {
          channel?: string;
          video?: string;
          collection?: string;
          parent: string[];
          autoplay?: boolean;
          muted?: boolean;
        },
      ) => ITwitchPlayer;
    };
  }
}

@Component({
  selector: 'app-twitch-player',
  styleUrl: './twitch-player.component.scss',
  templateUrl: './twitch-player.component.html',
})
export class TwitchPlayerComponent implements AfterViewInit, OnDestroy, IVideoPlayer {
  public readonly videoUrl = input.required<string>();
  public readonly autoPlay = input<boolean>(false);
  public readonly showControls = input<boolean>(true);

  public readonly playerStateChange = output<{
    isPlaying: boolean;
    currentTime: number;
    duration: number;
  }>();

  public readonly playerReady = output<boolean>();

  public readonly twitchPlayerContainer = viewChild<ElementRef>('twitchPlayerContainer');

  public readonly playerId = `twitch-player-${Math.random().toString(36).substr(2, 9)}`;

  private player: ITwitchPlayer | null = null;

  private currentTime = 0;
  private duration = 0;
  private playing = false;
  private volumeLevel = 1;
  private mutedState = false;

  private stateUpdateInterval: number | null = null;
  private initialPauseApplied = false;

  constructor() {
    effect(() => {
      if (this.videoUrl()) {
        this.initializePlayer();
      }
    });
  }

  public ngAfterViewInit(): void {
    this.initializePlayer();
  }

  public ngOnDestroy(): void {
    if (this.stateUpdateInterval) {
      window.clearInterval(this.stateUpdateInterval);
    }

    if (this.player) {
      this.player.pause();
      this.player = null;
    }

    this.removeTwitchScript();
  }

  public play(): void {
    if (this.player) {
      this.player.play();
      this.playing = true;
      this.emitStateChange();
    }
  }

  public pause(): void {
    if (this.player) {
      this.player.pause();
      this.playing = false;
      this.emitStateChange();
    }
  }

  public seek(time: number): void {
    if (this.player) {
      this.player.seek(time);
      this.currentTime = time;
      this.emitStateChange();
    }
  }

  public getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public isPlaying(): boolean {
    return this.playing;
  }

  public setVolume(volume: number): void {
    if (this.player) {
      this.player.setVolume(volume);
      this.volumeLevel = volume;

      if (volume === 0) {
        this.mutedState = true;
      } else if (this.mutedState) {
        this.mutedState = false;
      }
    }
  }

  public getVolume(): number {
    return this.volumeLevel;
  }

  public mute(): void {
    if (this.player) {
      this.player.setMuted(true);
      this.mutedState = true;
    }
  }

  public unmute(): void {
    if (this.player) {
      this.player.setMuted(false);
      this.mutedState = false;
    }
  }

  public isMuted(): boolean {
    return this.mutedState;
  }

  private initializePlayer(): void {
    this.removeTwitchScript();

    this.loadTwitchScript().then(() => {
      this.initPlayer();
      this.playerReady.emit(true);
    });
  }

  private loadTwitchScript(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (window.Twitch) {
        resolve();

        return;
      }

      const script = document.createElement('script');
      script.src = 'https://player.twitch.tv/js/embed/v1.js';
      script.async = true;
      script.onload = (): void => resolve();
      document.body.appendChild(script);
    });
  }

  private initPlayer(): void {
    if (!window.Twitch) {
      return;
    }

    const videoId = this.extractTwitchVideoId(this.videoUrl());
    const channelName = this.extractTwitchChannelName(this.videoUrl());

    if (!videoId && !channelName) {
      return;
    }

    const options = {
      autoplay: true,
      height: '100%',
      muted: true,
      parent: [window.location.hostname],
      width: '100%',
    };

    if (videoId) {
      Object.assign(options, { video: videoId });
    } else if (channelName) {
      Object.assign(options, { channel: channelName });
    }

    this.player = new window.Twitch.Player(this.playerId, options);

    this.player.addEventListener(get(window.Twitch, 'Player.PLAYING', ''), () => {
      if (!this.autoPlay() && !this.initialPauseApplied) {
        this.initialPauseApplied = true;
        this.player?.pause();
        this.player?.setMuted(false);

        return;
      }

      this.playing = true;
      this.emitStateChange();
    });

    this.player.addEventListener(get(window.Twitch, 'Player.PAUSE', ''), () => {
      this.playing = false;
      this.emitStateChange();
    });

    this.player.addEventListener(get(window.Twitch, 'Player.ENDED', ''), () => {
      this.playing = false;
      this.emitStateChange();
    });

    this.stateUpdateInterval = window.setInterval(() => {
      if (this.player) {
        this.currentTime = this.player.getCurrentTime() || 0;
        this.duration = this.player.getDuration() || 0;
        this.emitStateChange();
      }
    }, 1000);
  }

  private extractTwitchVideoId(url: string): string | null {
    const videoMatch = url.match(/twitch\.tv\/videos\/(\d+)/);

    return videoMatch ? videoMatch[1] : null;
  }

  private extractTwitchChannelName(url: string): string | null {
    const channelMatch = url.match(/twitch\.tv\/([^\/\?]+)/);

    return channelMatch ? channelMatch[1] : null;
  }

  private emitStateChange(): void {
    this.playerStateChange.emit({
      currentTime: this.currentTime,
      duration: this.duration,
      isPlaying: this.playing,
    });
  }

  private removeTwitchScript(): void {
    const twitchScript = document.querySelector('script[src="https://player.twitch.tv/js/embed/v1.js"]');

    if (twitchScript) {
      twitchScript.remove();
    }
  }
}
